import React from "react";
import {
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Modal,
  Stack,
  TableBody,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import usePanelAgendaData from "./usePanelAgenda";
import { useIsMobile } from "../../../../utils/hooks/useIsMobile";
import { useFullPageLoading } from "../../../../atoms/full-page-loading/loadingContext";
import { handleCopy } from "../../../../molecules/expert-profile-sections/profile-section/helper";
import { cellStyles, getStatusDisplay, stickyCellBase, truncateAgendaTitle, truncateExpertName } from "./helper";
import { 
  AgendaExpertPanelProps, 
  AgendaHeaderCellsProps, 
  ModalHeaderProps,
  ExpertNameCellProps,
  ActionsCellProps,
  StatusCellProps,
  AgendaRowProps
} from "./types";

// Sub-components
const ModalHeader: React.FC<ModalHeaderProps> = ({ onClose }) => (
  <Stack direction="row" justifyContent="space-between" px={2.5} py={1.5}>
    <Typography fontSize="16px" fontWeight={600} sx={{ opacity: 0.8 }}>
      Agenda Panel
    </Typography>
    <CloseIcon
      sx={{
        fontSize: 18,
        color: "#434343",
        opacity: 0.75,
        cursor: "pointer",
      }}
      onClick={onClose}
    />
  </Stack>
);

const ExpertNameCell: React.FC<ExpertNameCellProps> = ({ expertName, expertId }) => (
  <TableCell
    sx={{
      width: "150px",
      position: "sticky",
      left: 0,
      background: "#fff",
      zIndex: 1,
      padding: "6px",
      paddingLeft: "16px",
      borderRight: "1px solid rgba(224, 224, 224, 1)",
      height: 48,
      maxHeight: 48,
      overflow: "hidden",
    }}
  >
    <Box
      component="span"
      title={expertName}
      sx={{
        display: "inline-block",
        maxWidth: "120px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "middle",
      }}
    >
      <a
        href={`/layout/expert-profile?id=${expertId}&page=1`}
        target="_blank"
        style={{
          textDecoration: "underline",
          color: "#ec9324",
          display: "inline-block",
          maxWidth: "120px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          verticalAlign: "middle",
        }}
      >
        <Tooltip title={expertName} arrow>
          <Typography
          fontSize={"14px"}
          component="span"
          fontWeight={500}
          sx={{
            display: "inline-block",
            maxWidth: "120px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "middle",
          }}
        >
          {truncateExpertName(expertName)}
        </Typography>
        </Tooltip>
      </a>
    </Box>
  </TableCell>
);

const ActionsCell: React.FC<ActionsCellProps> = ({
  agendaItem,
  projectId,
  setLoading,
  refetch,
  openShareAgendaModal,
  enqueueSnackbar,
}) => (
  <TableCell
    sx={{
      minWidth: "90px",
      textAlign: "center",
      padding: "6px",
      position: "sticky",
      left: 150,
      background: "#fff",
      zIndex: 1,
      borderRight: "1px solid rgba(224, 224, 224, 1)",
    }}
  >
    <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
      <Tooltip title="Share Agenda" arrow>
        <IconButton
          color="inherit"
          onClick={() => {
            openShareAgendaModal({
              peId: agendaItem.id,
              projectId: String(projectId),
              setLoading: setLoading,
              refetch: refetch,
              handleClose: () => {},
            });
          }}
          size="small"
        >
          <ShareIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {(agendaItem.meta.project_invitation_link || agendaItem.meta.project_agenda_link) && (
        <Tooltip title={agendaItem.meta.project_invitation_link ? "Copy Invitation Link" : "Copy Agenda Link"} arrow>
          <IconButton
            color="inherit"
            onClick={() => {
              handleCopy(
                agendaItem.meta.project_invitation_link || agendaItem.meta.project_agenda_link,
                enqueueSnackbar,
                `${agendaItem.meta.project_invitation_link ? "Invitation Link" : "Agenda Link"} copied to clipboard`
              );
            }}
            size="small"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </TableCell>
);


const StatusCell: React.FC<StatusCellProps> = ({ agenda, agendaItem, projectId, openAgendaSubmitModal, refetch }) => {
  // Find the index of this agendaId in the expert's fk_agenda array
  const idx = Array.isArray(agendaItem.fk_agenda)
    ? agendaItem.fk_agenda.findIndex((id: number) => id === agenda.id)
    : -1;

  const status =
    idx !== -1 && Array.isArray(agendaItem.status)
      ? agendaItem.status[idx]
      : "";

  const { boxText, boxSx } = getStatusDisplay(status);
  // Check if this agenda's response is shared with client
  const isSharedWithClient =
    idx !== -1 && Array.isArray(agendaItem.is_shared_with_client)
      ? agendaItem.is_shared_with_client[idx]
      : false;

  return (
    <TableCell
      key={agenda.id}
      sx={{
        minWidth: "140px",
        textAlign: "center",
        padding: "6px",
        borderRight: "1px solid rgba(224, 224, 224, 1)",
      }}
    >
      <Box
        sx={boxSx}
        onClick={() => {
          if (status === "Sent" || status === "RespondedByAdmin" || status === "RespondedByExpert") {
            openAgendaSubmitModal({
              peId: agendaItem.id,
              projectId: projectId,
              refetch: refetch,
              handleClose: () => {
                refetch();
              },
              expertId: agendaItem.expert_id,
              expertInvitationStatus: agendaItem.expert_invitation,
              agendaId: agenda.id,
              openRespondedAgenda: status !== "Sent",
            });
          }
        }}
      >
        <Box display={"flex"} alignItems={"center"} justifyContent={'center'}>
          {isSharedWithClient && (
          <Tooltip title={"Shared with Client"} arrow>
            <FolderSharedIcon
            sx={{
              fontSize: 18,
              opacity: 0.8,
              marginLeft: "5px"
            }}
          />
          </Tooltip>
          
        )}
        <Typography fontSize={"12px"} fontWeight={600}>
          {boxText}
        </Typography>
        </Box>
      </Box>
    </TableCell>
  );
};

const AgendaRow: React.FC<AgendaRowProps> = ({
  agendaItem,
  rowIdx,
  agendaArray,
  projectId,
  setLoading,
  refetch,
  openShareAgendaModal,
  openAgendaSubmitModal,
  enqueueSnackbar,
}) => (
  <TableRow
    key={agendaItem.id || rowIdx}
    sx={{ overflowX: "scroll", height: 48, maxHeight: 48 }}
  >
    <ExpertNameCell
      expertName={agendaItem.expert_name}
      expertId={agendaItem.expert_id}
    />

    <ActionsCell
      agendaItem={agendaItem}
      projectId={projectId}
      setLoading={setLoading}
      refetch={refetch}
      openShareAgendaModal={openShareAgendaModal}
      enqueueSnackbar={enqueueSnackbar}
    />

    {agendaArray.map((agenda) => (
      <StatusCell
        key={agenda.id}
        agenda={agenda}
        agendaItem={agendaItem}
        projectId={projectId}
        openAgendaSubmitModal={openAgendaSubmitModal}
        refetch={refetch}
      />
    ))}
  </TableRow>
);

const AgendaHeaderCells: React.FC<AgendaHeaderCellsProps> = ({
  agendaArray,
  openAgendaDetailsModal,
}) => (
  <>
    {agendaArray.map(({ title, id, fk_project }) => {
      const displayTitle = truncateAgendaTitle(title);
      return (
        <TableCell
          key={id}
          sx={cellStyles}
          onClick={() =>
            openAgendaDetailsModal({
              projectId: String(fk_project),
              agendaId: String(id),
            })
          }
        >
          <Typography fontSize="14px" fontWeight={600}>
            {displayTitle}
          </Typography>
        </TableCell>
      );
    })}
  </>
);

// Main Component
const AgendaExpertPanel: React.FC<AgendaExpertPanelProps> = ({
  projectId,
  open,
  onClose,
  openAgendaDetailsModal,
  openShareAgendaModal,
  openAgendaSubmitModal,
}) => {
  const isMobile = useIsMobile();
  const { agendaArray, loading, filteredAgendaArray, refetch } =
    usePanelAgendaData({ projectId });
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = useFullPageLoading();

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: isMobile ? "100%" : "80%",
          maxHeight: "75vh",
          overflowY: "auto",
          bgcolor: "white",
          borderRadius: 2,
        }}
      >
        <ModalHeader onClose={onClose} />

        {loading ? (
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              height: "400px", 
              margin: "10px" 
            }}
          >
            <CircularProgress sx={{ color: '#EC9324' }} />
          </Box>
        ) : filteredAgendaArray?.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
              No Agenda Items Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no agenda items available for this project.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: "60vh",
              overflow: "auto",
              borderTop: "1px solid rgba(224, 224, 224, 1)",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{ tableLayout: "fixed", minWidth: 900 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      ...stickyCellBase,
                      width: "150px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      position: "sticky",
                      left: 0,
                    }}
                  >
                    <Typography fontSize="14px" fontWeight={600}>
                      Expert Name
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...stickyCellBase,
                      width: "90px",
                      textAlign: "center",
                      position: "sticky",
                      left: 150,
                    }}
                  >
                    <Typography fontSize="14px" fontWeight={600}>
                      Actions
                    </Typography>
                  </TableCell>

                  <AgendaHeaderCells
                    openAgendaDetailsModal={openAgendaDetailsModal}
                    agendaArray={agendaArray}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAgendaArray?.map((agendaItem: any, rowIdx: number) => (
                  <AgendaRow
                    key={agendaItem.id || rowIdx}
                    agendaItem={agendaItem}
                    rowIdx={rowIdx}
                    agendaArray={agendaArray}
                    projectId={projectId}
                    setLoading={setLoading}
                    refetch={refetch}
                    openShareAgendaModal={openShareAgendaModal}
                    openAgendaSubmitModal={openAgendaSubmitModal}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Modal>
  );
};



export default AgendaExpertPanel;
